import { useState, useEffect, useCallback } from 'react';
import { ApiError } from '../types';

export interface StatsBiggestMover {
  video_id: string;
  title: string | null;
  channel_title: string | null;
  thumbnail_url: string | null;
  region: string;
  category_id: string;
  delta_views: number;
  delta_per_hour: number;
}

export interface StatsAllTimeTop {
  video_id: string;
  title: string;
  artist: string;
  wiki_view_count: number;
}

export interface StatsData {
  videosTracked: number;
  totalViewsTracked: number;
  snapshotsRecorded: number;
  daysCollecting: number;
  topCategories: { categoryId: string; deltaViews: number }[];
  topRegions: { region: string; deltaViews: number }[];
  biggestMover: StatsBiggestMover | null;
  allTimeTop: StatsAllTimeTop | null;
  generatedAt: number;
}

interface UseStatsResult {
  stats: StatsData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetch the /stats dashboard's aggregate numbers. `enabled` gates the request
 * so the app only fetches while the Stats view is actually active.
 */
export function useStats(enabled = true): UseStatsResult {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/stats');
      if (!response.ok) {
        const errorData = (await response.json()) as ApiError;
        throw new Error(errorData.error || 'Failed to fetch stats');
      }
      setStats((await response.json()) as StatsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setStats(null);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, isLoading, error, refetch: fetchStats };
}
